// eslint-disable-next-line no-unused-vars
import React, { memo, useRef, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSWRConfig } from 'swr';
import { stringify } from 'query-string';
import debounce from 'lodash/debounce';
import omit from 'lodash/omit';
import { useContextSelector } from 'use-context-selector';

import useRouter from 'Hooks/useRouter';
import useFetch from 'Hooks/useFetch';
import useUser from 'Hooks/useUser';
import useScrollLock from 'Hooks/useScrollLock';
import { CartContext } from 'Contexts/CartContext';
import { UIContext } from 'Contexts/UIContext';

import { API_PRODUCTS, API_PRODUCTS_SEARCH } from 'Constants';
import SearchComponent from 'Components/Search/Search';
import useModals from 'Hooks/useModals';
import useToastNotifications from 'Hooks/useToastNotifications';
import useAnalytics from 'Hooks/useAnalytics';

const defaultState = {
  products: [],
  categories: []
};
const MAX_SEARCH_LENGTH = 100;

const Search = memo(() => {
  const { t } = useTranslation();
  const fetch = useFetch();
  const { cache } = useSWRConfig();
  const { data: user } = useUser();
  const { location, query, push } = useRouter();
  const analytics = useAnalytics();
  const ref = useRef(null);
  const modalsContext = useModals();
  const setUiState = useContextSelector(UIContext, data => data.setUiState);
  const toast = useToastNotifications();
  const [open, setOpen] = useState(location.hash.includes('opensearch'));
  const [addToCartLoading, setAddToCartLoading] = useState(false);
  const { data: cart, updateProduct: updateCartProduct } = useContextSelector(
    CartContext, data => data
  );
  const [onlyAvailable, setOnlyAvailable] = useState(
    parseInt(query?.only_available, 10) === 1
  );
  const [searchResults, setSearchResults] = useState(
    location?.state?.searchResults
    || defaultState
  );
  const [isLoading, setLoading] = useState(false);
  const searchMatch = useRef(location.pathname.match(/\/search\/([\w+\d+\s+"%,=&-]*)\/?/));
  const [currentSearch, setSearch] = useState(
    decodeURI(searchMatch?.current?.at(1) || '').slice(0, MAX_SEARCH_LENGTH)
  );

  const cartItemsRef = useRef(cart.items || []);

  useEffect(() => {
    cartItemsRef.current = cart.items || [];
  }, [cart.items]);

  useEffect(() => {
    if (
      open
      && !location.hash.includes('opensearch')
    ) {
      push({
        pathname: location.pathname,
        search: location.search,
        hash: 'opensearch'
      });
    } else if (
      !open
      && location.hash.includes('opensearch')
    ) {
      push({
        pathname: location.pathname,
        search: location.search
      });
    }
    setUiState({ searchOpen: open });
  }, [open, location.hash]);

  useEffect(() => {
    if (currentSearch && searchResults !== defaultState) {
      debouncedProductFetch(currentSearch, onlyAvailable, user?.defaultCurrency, user?.withVat);
    }
  }, [user?.defaultCurrency, user?.withVat]);

  const handleSearchSubmit = useCallback(search => {
    const normalizedSearch = (search || '').trim();

    if (normalizedSearch.length < 3) {
      return;
    }

    if (open) {
      setOpen(false);
    }

    push({
      pathname: `/products/search/${ encodeURIComponent(normalizedSearch) }/c`,
      search: stringify(omit({
        ...query,
        order_by: query?.order_by || 'relevance',
        order_direction: query?.order_direction || 'asc'
      }, ['page', 'slug', 'id'])),
      state: {
        searchResults: cache.get(API_PRODUCTS_SEARCH)
      }
    });
  }, [cache, open, push, query]);

  const handleStockAlertModalOpen = product => {
    analytics.track('opened_stock_alert_modal', {
      from: 'search', product_id: product.id || product.productId
    });
    modalsContext.setState(modalsContext.modalState.STOCK_ALERT.key, {
      open: true,
      productId: product.id || product.productId,
      productCode: product.code,
      productQuantity: Math.max(product.quantity, product.minQuantity)
    });
  };

  const handleOnAddToCart = async product => {
    if (!product.hasPrice) {
      modalsContext.setState(modalsContext.modalState.ADD_EXTERNAL_SUPPLIER_PRODUCT_TO_CART.key, {
        open: true,
        product,
        onConfirm: () => executeAddToCart(product)
      });
      return null;
    }

    return executeAddToCart(product);
  };

  const executeAddToCart = async product => {
    const cartProduct = cartItemsRef.current.find(item => item.productId === product.id);

    const quantity = Math.max(
      product.minQuantity,
      Math.min(
        product.stockValue,
        (cartProduct?.quantity || 0) + (product.minQuantity || 1)
      )
    );
    setAddToCartLoading(product.id);
    try {
      const [updated, crossProducts] = await Promise.allSettled([
        updateCartProduct({
          ...product,
          id: product.id || product.productId,
          quantity
        }),
        fetch(`${API_PRODUCTS}/${product.id}/cross-sells?currency=${ user?.defaultCurrency }&vat=${ user?.withVat }`)
      ]);
      if (updated.status === 'fulfilled') {
        toast.addNotification({
          title: t('cart.successfullyAddedToCart')
        });
      } else {
        toast.addNotification({
          type: 'error',
          title: 'cart.failedAddToCart',
          message: updated.value?.errors?.at(0)?.message
        });
        return null;
      }
      if (
        Array.isArray(crossProducts.value)
        && crossProducts.value.length
      ) {
        modalsContext.setState(modalsContext.modalState.CROSS_SALES.key, {
          open: true,
          products: crossProducts.value,
          product: {
            ...product,
            quantity
          }
        });
      }
      return updated.value;
    } catch (err) {
      toast.addNotification({
        type: 'error',
        title: 'cart.failedAddToCart',
        message: err?.info?.errors?.at(0)?.message
      });
    } finally {
      setAddToCartLoading(false);
    }
    return null;
  };

  const handleOnEnter = useCallback(
    evt => {
      if (evt?.preventDefault) {
        evt.preventDefault();
      }
      handleSearchSubmit(currentSearch);
    },
    [currentSearch, handleSearchSubmit]
  );

  const handleOnKeyDown = useCallback(
    evt => {
      if (evt?.keyCode === 13) {
        handleSearchSubmit(currentSearch);
      }
      if (evt?.keyCode === 27) {
        setOpen(false);
        setTimeout(() => {
          // IMPORTANT ! the escape button clears the input, we don't want that!
          setSearch(currentSearch);
        }, 0);
      }
    },
    [currentSearch, handleSearchSubmit]
  );

  const handleOnSearchClick = useCallback(
    () => {
      if (currentSearch?.length >= 3) {
        setOpen(true);
        if (searchResults === defaultState) {
          fetchProducts(currentSearch);
        }
      }
    },
    [currentSearch, searchResults]
  );

  const handleOnClear = useCallback(
    debounce(() => {
      analytics.track('cleared_search', { cleared_term: currentSearch });
      setSearch('');
      ref.current.querySelector('input').blur();
    }, 250, { leading: false, trailing: true }));

  const fetchProducts = async (search, available, defaultCurrency, withVat) => {
    if (!search || search?.length < 3) return;
    try {
      setLoading(true);
      let searchString = `${ API_PRODUCTS_SEARCH }?search=${ encodeURIComponent(search) }&currency=${ defaultCurrency || user?.defaultCurrency }&vat=${ withVat || user?.withVat }`;
      if (available) {
        searchString += `&only_available=${ available ? 1 : 0 }`;
      }
      const searchedProducts = await fetch(searchString, {
        cacheKey: 'search'
      });
      analytics.track('searched_products', {
        searched_term: search,
        has_results: !!(searchedProducts?.products?.length),
        only_available: available
      });
      if (searchedProducts) {
        cache.set(API_PRODUCTS_SEARCH, searchedProducts);
        if (!searchedProducts?.products?.length) {
          setSearchResults({
            ...defaultState,
            error: t('common.search.noResults')
          });
        } else {
          setSearchResults(searchedProducts);
        }
        setLoading(false);
      }
    } catch (err) {
      cache.delete(API_PRODUCTS_SEARCH);
      setSearchResults({
        products: [],
        categories: [],
        error: err?.error?.message || err.message
      });
      setLoading(false);
    }
  };

  const debouncedProductFetch = useCallback(
    debounce(fetchProducts, 250, { leading: false, trailing: true }),
    [user?.defaultCurrency, user?.withVat]
  );

  const toggleOnlyAvailable = useCallback(
    checked => {
      setOnlyAvailable(prevState => {
        if (checked !== prevState) {
          debouncedProductFetch(currentSearch.trim(), checked, user?.defaultCurrency, user?.withVat);
          return checked;
        }
        return prevState;
      });
    },
    [currentSearch]
  );

  useEffect(() => {
    return () => {
      debouncedProductFetch.cancel();
    };
  }, []);

  const handleOnChange = useCallback(
    evt => {
      if (evt?.preventDefault) {
        evt.preventDefault();
      }
      const inputTextValue = evt?.target?.value?.slice(0, MAX_SEARCH_LENGTH);
      if (inputTextValue?.trim().length >= 3) {
        setOpen(true);
      }
      setSearch(prevSearch => {
        if (inputTextValue !== prevSearch) {
          const trimmedInputValue = inputTextValue?.trim() || '';
          if (trimmedInputValue !== prevSearch) {
            debouncedProductFetch(trimmedInputValue, onlyAvailable, user?.defaultCurrency, user?.withVat);
          }
          return inputTextValue || '';
        }
        return prevSearch;
      });
    },
    [onlyAvailable, debouncedProductFetch, user?.defaultCurrency, user?.withVat]
  );

  useEffect(() => {
    if (currentSearch?.length < 3) {
      if (isLoading) {
        setLoading(false);
      }
      if (open) {
        setOpen(false);
      }
    }
  }, [currentSearch, isLoading, open]);

  useScrollLock(open);

  useEffect(() => {
    const handleOnClick = evt => {
      if (
        open
        && !ref.current.contains(evt.target)
      ) {
        setOpen(false);
      }
    };

    if (typeof document !== 'undefined') {
      document.addEventListener('click', handleOnClick);
    }
    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('click', handleOnClick);
      }
    };
  }, [open]);

  const handleOnClick = useCallback(() => {
    debouncedProductFetch.cancel();
    setSearch('');
    setSearchResults(defaultState);
    setTimeout(() => {
      setOpen(false);
    }, 0);
  }, [debouncedProductFetch]);

  return (
    <SearchComponent
      ref={ ref }
      onClick={ handleOnClick }
      currency={ user?.defaultCurrency }
      withVat={ user?.withVat }
      onEnter={ handleOnEnter }
      onChange={ handleOnChange }
      onKeyDown={ handleOnKeyDown }
      onToggleAvailable={ toggleOnlyAvailable }
      onSearchClick={ handleOnSearchClick }
      isValid={ !currentSearch?.length || currentSearch?.length >= 3 }
      onAddToCart={ handleOnAddToCart }
      onStockAlertModalOpen={ handleStockAlertModalOpen }
      search={ currentSearch }
      searchResults={ searchResults }
      onlyAvailable={ onlyAvailable }
      isDropdownOpen={ open }
      isLoading={ isLoading }
      addToCartLoading={ addToCartLoading }
      onClear={ handleOnClear }
      accountManagerPhone={ user?.accountManager?.phone }
    />
  );
});

Search.displayName = 'SearchContainer';

export default Search;
